<template>
  <div class="notification-template-viewer">
    <!-- Header Section -->
    <div class="max-w-[48rem] lg:max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto">
      <div class="text-center mb-8">
        <div class="mb-3 inline-block px-4 py-1">
          <p class="text-blue-400 font-medium">Notification Manager</p>
        </div>
        <h1 class="text-3xl md:text-5xl font-bold text-white mb-3 bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent">
          Customer Notifications
        </h1>
        <p class="text-gray-300 text-lg mb-6">
          View and manage email and SMS notification templates
        </p>
        <div class="flex flex-col sm:flex-row gap-4 items-center justify-center">
          <button 
            @click="refreshNotifications" 
            :disabled="isLoading"
            class="bg-blue-600/80 backdrop-blur-lg text-white px-6 py-3 rounded-2xl hover:bg-blue-700/80 disabled:opacity-50 flex items-center space-x-2 border border-white/20 shadow-2xl transition-all duration-300 hover:scale-105">
            <svg v-if="isLoading" class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            <span>{{ isLoading ? 'Loading...' : 'Refresh' }}</span>
          </button>
          
        </div>
      </div>
    </div>

    <!-- Current Account Form Info -->
    <div class="max-w-[48rem] lg:max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto mb-8">
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-2xl">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-sm font-medium text-gray-300 mb-1">Notification Templates for Your Account</h3>
            <p class="text-lg font-semibold text-white">
              {{ currentAccountName }}
              <span v-if="currentAccountForm" class="text-gray-400 text-sm ml-2">(Form ID: {{ currentAccountForm.id }})</span>
            </p>
          </div>
          <span class="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-500/20 text-blue-300 border border-blue-400/30">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Your Forms
          </span>
        </div>
      </div>
    </div>

    <!-- Notifications List -->
    <div v-if="notifications.length > 0" class="max-w-[48rem] lg:max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto space-y-6">
      <div 
        v-for="notification in notifications" 
        :key="notification.id"
        class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-2xl cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:bg-black/70"
        @click="toggleNotification(notification.id)">
        
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <div class="flex items-center space-x-3">
              <div class="flex-shrink-0">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                      :class="notification.isActive ? 'bg-green-500/20 text-green-300 border border-green-400/30' : 'bg-red-500/20 text-red-300 border border-red-400/30'">
                  <div class="w-2 h-2 rounded-full mr-2"
                       :class="notification.isActive ? 'bg-green-400 animate-pulse' : 'bg-red-400'"></div>
                  {{ notification.isActive ? 'Active' : 'Inactive' }}
                </span>
              </div>
              <div>
                <h3 class="text-lg font-medium text-white">{{ notification.name }}</h3>
                <p class="text-sm text-gray-300">{{ notification.subject }}</p>
              </div>
            </div>
            
            <div class="mt-2 flex items-center space-x-4 text-sm text-gray-400">
              <span>To: {{ notification.to || 'Not specified' }}</span>
              <span>From: {{ notification.from || 'Not specified' }}</span>
            </div>
          </div>
          
          <div class="flex-shrink-0">
            <svg 
              :class="{ 'rotate-180': expandedNotifications[notification.id] }" 
              class="h-5 w-5 text-gray-300 transition-transform duration-200" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <!-- Expanded Notification Details -->
        <div v-if="expandedNotifications[notification.id]" class="mt-6 border-t border-white/20 pt-6" @click.stop>
          
          <!-- Template Preview Tabs -->
          <div class="border-b border-white/20">
            <nav class="-mb-px flex space-x-8">
              <button
                @click.stop="setActiveTab(notification.id, 'preview')"
                :class="getTabClasses(notification.id, 'preview')"
                class="py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200">
                Preview
              </button>
              <button
                @click.stop="setActiveTab(notification.id, 'html')"
                :class="getTabClasses(notification.id, 'html')"
                class="py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200">
                HTML Source
              </button>
              <button
                @click.stop="setActiveTab(notification.id, 'settings')"
                :class="getTabClasses(notification.id, 'settings')"
                class="py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200">
                Settings
              </button>
            </nav>
          </div>

          <!-- Tab Content -->
          <div class="mt-6">
            
            <!-- Preview Tab -->
            <div v-if="getActiveTab(notification.id) === 'preview'" class="space-y-4">
              <div class="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
                <h4 class="text-sm font-medium text-gray-300 mb-3">{{ getPreviewTitle(notification) }}</h4>
                <div class="border border-white/20 rounded-xl overflow-hidden">
                  <iframe 
                    :srcdoc="processEmailTemplate(notification.message, notification)"
                    class="w-full border-0 bg-white"
                    style="width: 100%; height: auto; min-height: 600px;"
                    sandbox="allow-same-origin"
                    @load="adjustIframeHeight">
                  </iframe>
                </div>
              </div>
            </div>



            <!-- HTML Source Tab -->
            <div v-if="getActiveTab(notification.id) === 'html'" class="space-y-4">
              <div class="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
                <div class="flex items-center justify-between mb-3">
                  <h4 class="text-sm font-medium text-gray-300">HTML Source Code</h4>
                  <button 
                    @click.stop="copyToClipboard(notification.message)"
                    class="text-sm text-blue-400 hover:text-blue-300 flex items-center space-x-1 bg-blue-500/20 px-3 py-1 rounded-lg border border-blue-400/30 transition-colors duration-200">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                    </svg>
                    <span>Copy</span>
                  </button>
                </div>
                <pre class="bg-gray-900/50 border border-white/10 p-4 rounded-xl text-sm overflow-x-auto max-h-96 overflow-y-auto"><code class="text-gray-300">{{ notification.message }}</code></pre>
              </div>
            </div>

            <!-- Settings Tab -->
            <div v-if="getActiveTab(notification.id) === 'settings'" class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
                  <h4 class="text-sm font-medium text-gray-300 mb-3">Basic Settings</h4>
                  <dl class="space-y-3 text-sm">
                    <div>
                      <dt class="font-medium text-gray-400">Name:</dt>
                      <dd class="text-gray-300 mt-1">{{ notification.name }}</dd>
                    </div>
                    <div>
                      <dt class="font-medium text-gray-400">Subject:</dt>
                      <dd class="text-gray-300 mt-1">{{ notification.subject }}</dd>
                    </div>
                    <div>
                      <dt class="font-medium text-gray-400">Status:</dt>
                      <dd class="mt-1">
                        <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                              :class="notification.isActive ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'">
                          <div class="w-2 h-2 rounded-full mr-2"
                               :class="notification.isActive ? 'bg-green-400' : 'bg-red-400'"></div>
                          {{ notification.isActive ? 'Active' : 'Inactive' }}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>
                
                <div class="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
                  <h4 class="text-sm font-medium text-gray-300 mb-3">Recipients</h4>
                  <dl class="space-y-3 text-sm">
                    <div>
                      <dt class="font-medium text-gray-400">To:</dt>
                      <dd class="text-gray-300 mt-1">{{ notification.to || 'Not specified' }}</dd>
                    </div>
                    <div>
                      <dt class="font-medium text-gray-400">From:</dt>
                      <dd class="text-gray-300 mt-1">{{ notification.from || 'Not specified' }}</dd>
                    </div>
                    <div>
                      <dt class="font-medium text-gray-400">Reply To:</dt>
                      <dd class="text-gray-300 mt-1">{{ notification.replyTo || 'Not specified' }}</dd>
                    </div>
                    <div>
                      <dt class="font-medium text-gray-400">BCC:</dt>
                      <dd class="text-gray-300 mt-1">{{ notification.bcc || 'Not specified' }}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SMS Preview Section -->
    <div v-if="notifications.length > 0" class="max-w-[48rem] lg:max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto mt-6">
      <div 
        class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-2xl cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:bg-black/70"
        @click="toggleSMSPreview()">
        
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <div class="flex items-center space-x-3">
              <div class="flex-shrink-0">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-400/30">
                  <div class="w-2 h-2 rounded-full mr-2 bg-blue-400 animate-pulse"></div>
                  SMS
                </span>
              </div>
              <div>
                <h3 class="text-lg font-medium text-white">SMS Notification</h3>
                <p class="text-sm text-gray-300">Text message welcome notification</p>
              </div>
            </div>
            
            <div class="mt-2 flex items-center space-x-4 text-sm text-gray-400">
              <span>To: {{ getSMSToNumber() }}</span>
              <span>From: {{ getSMSFromNumber() }}</span>
            </div>
          </div>
          
          <div class="flex-shrink-0">
            <svg 
              :class="{ 'rotate-180': showSMSPreview }" 
              class="h-5 w-5 text-gray-300 transition-transform duration-200" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <!-- Expanded SMS Preview -->
        <div v-if="showSMSPreview" class="mt-6 border-t border-white/20 pt-6">
          <div class="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
            <h4 class="text-sm font-medium text-gray-300 mb-3">SMS Preview</h4>
            <div class="max-w-sm mx-auto">
              <!-- Mobile Phone Frame -->
              <div class="relative">
                <!-- Phone Frame -->
                <div class="bg-black rounded-[2.5rem] p-2 shadow-2xl">
                  <!-- Screen -->
                  <div class="bg-gray-900 rounded-[2rem] overflow-hidden">
                    <!-- Status Bar -->
                    <div class="bg-black px-6 py-2 flex justify-between items-center text-white text-sm">
                      <div class="flex items-center space-x-1">
                        <div class="flex space-x-1">
                          <div class="w-1 h-1 bg-white rounded-full"></div>
                          <div class="w-1 h-1 bg-white rounded-full"></div>
                          <div class="w-1 h-1 bg-white/50 rounded-full"></div>
                        </div>
                        <span class="ml-2 text-xs">engagr</span>
                      </div>
                      <div class="text-xs">{{ getCurrentTime() }}</div>
                      <div class="flex items-center space-x-1">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 01.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.414 5 5 0 017.07 0 1 1 0 01-1.415 1.414zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd"></path>
                        </svg>
                        <div class="text-xs">100%</div>
                      </div>
                    </div>
                    
                    <!-- Messages Header -->
                    <div class="bg-gray-800 px-4 py-3 border-b border-gray-700 flex items-center">
                      <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                        {{ currentBrand.charAt(0) }}
                      </div>
                      <div class="flex-1">
                        <div class="text-white font-semibold text-sm">{{ currentAccountName }}</div>
                        <div class="text-gray-400 text-xs">Active now</div>
                      </div>
                      <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                      </svg>
                    </div>
                    
                    <!-- Messages Container -->
                    <div class="bg-black min-h-[400px] p-4 space-y-3">
                      <!-- SMS Message Bubble -->
                      <div class="flex justify-start">
                        <div class="max-w-xs lg:max-w-md">
                          <div class="bg-gray-700 text-white p-3 rounded-2xl rounded-bl-md shadow-lg">
                            <div class="text-sm leading-relaxed">
                              {{ processSMSTemplate() }}
                            </div>
                          </div>
                          <div class="text-xs text-gray-500 mt-1 px-2">
                            {{ getCurrentTime() }} • {{ getSMSFromNumber() }}
                          </div>
                        </div>
                      </div>
                      
                      <!-- Delivery Status -->
                      <div class="text-center">
                        <div class="inline-flex items-center text-xs text-gray-500 bg-gray-800/50 px-3 py-1 rounded-full">
                          <div class="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                          Delivered
                        </div>
                      </div>
                    </div>
                    
                    <!-- Message Input Area -->
                    <div class="bg-gray-800 p-3 border-t border-gray-700">
                      <div class="flex items-center space-x-2">
                        <div class="flex-1 bg-gray-700 rounded-full px-4 py-2 text-gray-400 text-sm">
                          Message
                        </div>
                        <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.429a1 1 0 001.17-1.409l-7-14z"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- SMS Settings Info -->
            <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl p-3">
                <h5 class="text-xs font-medium text-gray-400 mb-2">From Number</h5>
                <p class="text-sm text-gray-300">{{ getSMSFromNumber() }}</p>
              </div>
              <div class="bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl p-3">
                <h5 class="text-xs font-medium text-gray-400 mb-2">To Number</h5>
                <p class="text-sm text-gray-300">{{ getSMSToNumber() }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="selectedFormId && !isLoading" class="max-w-[48rem] lg:max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto">
      <div class="text-center py-12">
        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-8 shadow-2xl">
          <div class="mx-auto w-16 h-16 bg-gradient-to-t from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m8-5a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-white mb-2">No notifications found</h3>
          <p class="text-gray-300">This form doesn't have any notifications configured yet.</p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="isLoading" class="max-w-[48rem] lg:max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto">
      <div class="text-center py-12">
        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-8 shadow-2xl">
          <div class="inline-flex items-center space-x-3">
            <svg class="animate-spin h-8 w-8 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="text-lg font-medium text-white">Loading notifications...</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Initial State -->
    <div v-else class="max-w-[48rem] lg:max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto">
      <div class="text-center py-12">
        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-8 shadow-2xl">
          <div class="mx-auto w-16 h-16 bg-gradient-to-t from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-white mb-2">Loading Account Data</h3>
          <p class="text-gray-300">Preparing your notification templates...</p>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div 
      v-if="showToast"
      class="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-opacity duration-300">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'

export default {
  name: 'NotificationTemplateViewer',
  data() {
    return {
      isLoading: false,
      selectedFormId: '',
      availableForms: [],
      notifications: [],
      expandedNotifications: {},
      activeTabs: {},
      showToast: false,
      toastMessage: '',
      showSMSPreview: false, // SMS accordion state
      recentCustomerPhoto: null, // Store recent customer photo
      latestSubmissionData: null, // Store the latest form submission
      credentials: {
        consumerKey: 'ck_9eb5fc380da548b29cd454ad4e4e40edbf6f8aad',
        consumerSecret: 'cs_15706f39bc2a94c26ad71e695a2e730e29f6a7ae'
      }
    }
  },
  computed: {
    ...mapState(['accountData']),
    currentAccountFormId() {
      return this.accountData && this.accountData[0]?.form ? this.accountData[0].form : null
    },
    currentAccountForm() {
      if (!this.currentAccountFormId) return null
      return this.availableForms.find(form => form.id == this.currentAccountFormId) || null
    },
    currentAccountName() {
      return this.accountData && this.accountData[0]?.name ? this.accountData[0].name : this.dealerNameFromSlug
    },
    dealerNameFromSlug() {
      // Get dealer name from slug - convert slug format to display format
      const slug = this.$route.params.slug;
      if (slug) {
        // Convert slug like "peter-davey-suzuki" to "Peter Davey Suzuki"
        return slug.split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      return 'Dealership';
    },
    currentBrand() {
      // Extract brand from dealer name or slug
      const name = this.currentAccountName.toLowerCase();
      if (name.includes('honda')) return 'Honda';
      if (name.includes('suzuki')) return 'Suzuki';
      if (name.includes('ford')) return 'Ford';
      if (name.includes('toyota')) return 'Toyota';
      if (name.includes('mazda')) return 'Mazda';
      if (name.includes('hyundai')) return 'Hyundai';
      if (name.includes('kia')) return 'Kia';
      // Add more brands as needed
      return 'Auto';
    },
    brandColor() {
      // Return brand-specific colors
      const brand = this.currentBrand.toLowerCase();
      switch (brand) {
        case 'honda': return '#cc0000';
        case 'suzuki': return '#E4002B';
        case 'ford': return '#0066CC';
        case 'toyota': return '#CC0000';
        case 'mazda': return '#CC0000';
        case 'hyundai': return '#002C5F';
        case 'kia': return '#05141F';
        default: return '#cc0000';
      }
    }
  },
  watch: {
    accountData: {
      handler(newVal) {
        if (newVal && newVal[0]?.form) {
          // Fetch the current account's form and notifications
          this.fetchForms().then(() => {
            this.fetchNotifications()
          })
        }
      },
      immediate: true
    }
  },
  async mounted() {
    // Fetch account data if not already loaded
    if (!this.accountData) {
      await this.$store.dispatch('fetchAccountData', this.$route.params.slug)
    }
    
    // Only fetch forms after we have account data
    if (this.currentAccountFormId) {
      await this.fetchForms()
    }
  },
  methods: {
    async fetchForms() {
      // Only fetch the current account's form for security
      if (!this.currentAccountFormId) {
        return
      }

      this.isLoading = true
      try {
        const authString = btoa(`${this.credentials.consumerKey}:${this.credentials.consumerSecret}`)
        
        // Fetch only the current account's form
        const response = await axios.get(`https://engagr.driveagent.io/wp-json/gf/v2/forms/${this.currentAccountFormId}`, {
          headers: {
            'Authorization': `Basic ${authString}`,
          },
        })
        
        const formData = response.data
        
        if (formData && formData.id) {
          this.availableForms = [{
            id: formData.id,
            title: formData.title || `Form ${formData.id}`
          }]
          
          // Auto-select this form
          this.selectedFormId = formData.id.toString()
        } else {
          this.availableForms = []
          this.showToastMessage('Form data not accessible')
        }
      } catch (error) {
        if (error.response?.status === 403 || error.response?.status === 401) {
          this.showToastMessage('Access denied to form data')
        } else {
          this.showToastMessage(`Failed to load form: ${error.response?.status || error.message}`)
        }
      } finally {
        this.isLoading = false
      }
    },

    async fetchNotifications() {
      if (!this.selectedFormId) return
      
      this.isLoading = true
      this.notifications = []
      
      try {
        const authString = btoa(`${this.credentials.consumerKey}:${this.credentials.consumerSecret}`)
        
        // Fetch form with notifications
        const response = await axios.get(`https://engagr.driveagent.io/wp-json/gf/v2/forms/${this.selectedFormId}`, {
          headers: {
            'Authorization': `Basic ${authString}`,
          },
        })
        
        // Extract notifications from the form data
        if (response.data.notifications) {
          this.notifications = Object.values(response.data.notifications).map(notification => ({
            id: notification.id,
            name: notification.name || 'Untitled Notification',
            subject: notification.subject || 'No Subject',
            message: notification.message || '',
            to: notification.to || '',
            from: notification.from || '',
            fromName: notification.fromName || '',
            replyTo: notification.replyTo || '',
            bcc: notification.bcc || '',
            isActive: notification.isActive === '1',
            ...notification
          }))
          
          // Expand the first notification by default
          if (this.notifications.length > 0) {
            this.$set(this.expandedNotifications, this.notifications[0].id, true)
          }
        }
      } catch (error) {
        this.showToastMessage(`Failed to fetch notifications: ${error.response?.status || error.message}`)
      } finally {
        this.isLoading = false
      }
      
      // Fetch recent customer photo and latest submission data for template preview
      this.latestSubmissionData = await this.fetchLatestSubmissionData()
      this.recentCustomerPhoto = this.latestSubmissionData?.['13'] || await this.fetchRecentCustomerPhoto()
    },

    refreshNotifications() {
      this.fetchNotifications()
    },

    toggleNotification(notificationId) {
      this.$set(this.expandedNotifications, notificationId, !this.expandedNotifications[notificationId])
      
      // Set default active tab when expanding
      if (this.expandedNotifications[notificationId] && !this.activeTabs[notificationId]) {
        this.$set(this.activeTabs, notificationId, 'preview')
      }
    },

    toggleSMSPreview() {
      this.showSMSPreview = !this.showSMSPreview
    },

    setActiveTab(notificationId, tab) {
      this.$set(this.activeTabs, notificationId, tab)
    },

    getActiveTab(notificationId) {
      return this.activeTabs[notificationId] || 'preview'
    },

    getTabClasses(notificationId, tab) {
      const isActive = this.getActiveTab(notificationId) === tab
      return isActive 
        ? 'border-blue-400 text-blue-400' 
        : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
    },

    getPreviewTitle(notification) {
      // Check if this is a social media notification
      const isSocialNotification = notification && 
        (notification.name && notification.name.toLowerCase().includes('social')) ||
        (notification.subject && notification.subject.toLowerCase().includes('social')) ||
        (notification.subject && notification.subject.toLowerCase().includes('facebook'))
      
      return isSocialNotification ? 'Social Post Preview' : 'Email Preview'
    },

    async fetchLatestSubmissionData() {
      if (!this.currentAccountFormId) return null
      
      try {
        // Fetch the most recent submission
        const authString = btoa(`${this.credentials.consumerKey}:${this.credentials.consumerSecret}`)
        const response = await axios.get(`https://engagr.driveagent.io/wp-json/gf/v2/forms/${this.currentAccountFormId}/entries`, {
          headers: { 'Authorization': `Basic ${authString}` },
          params: {
            paging: { page_size: 1 }, // Get only the latest entry
            sorting: { key: 'date_created', direction: 'DESC' }
          }
        })
        
        if (response.data && response.data.entries && response.data.entries.length > 0) {
          const latestEntry = response.data.entries[0]
          return latestEntry
        }
      } catch (error) {
        // Silently handle error
      }
      
      return null
    },

    async fetchRecentCustomerPhoto() {
      if (!this.currentAccountFormId) return null
      
      try {
        // Fetch recent entries to get a customer photo
        const authString = btoa(`${this.credentials.consumerKey}:${this.credentials.consumerSecret}`)
        const response = await axios.get(`https://engagr.driveagent.io/wp-json/gf/v2/forms/${this.currentAccountFormId}/entries`, {
          headers: { 'Authorization': `Basic ${authString}` },
          params: {
            paging: { page_size: 10 }, // Get last 10 entries
            sorting: { key: 'date_created', direction: 'DESC' }
          }
        })
        
        if (response.data && response.data.entries) {
          // Look for entries with photo uploads
          for (const entry of response.data.entries) {
            // Look for signature or photo fields (these are typically file uploads)
            for (const value of Object.values(entry)) {
              if (value && typeof value === 'string' && 
                  (value.includes('.jpg') || value.includes('.png') || value.includes('.jpeg') || value.includes('.gif')) &&
                  value.startsWith('http')) {
                return value // Return the first valid image URL found
              }
            }
          }
        }
      } catch (error) {
        // Silently handle error
      }
      
      return null
    },

    processEmailTemplate(htmlContent, notification = null) {
      if (!htmlContent) return ''
      
      // Check if this is a social media notification
      const isSocialNotification = notification && 
        (notification.name && notification.name.toLowerCase().includes('social')) ||
        (notification.subject && notification.subject.toLowerCase().includes('social')) ||
        (notification.subject && notification.subject.toLowerCase().includes('facebook'))
      
      // If we have latest submission data AND this is a social notification, create a Facebook-style post
      if (this.latestSubmissionData && isSocialNotification) {
        const customerName = this.latestSubmissionData['1'] || 'Customer'
        const vehicle = this.latestSubmissionData['4'] || 'New Vehicle'
        const customerPhoto = this.latestSubmissionData['13'] || this.recentCustomerPhoto
        
        // Create Facebook-style post HTML
        const facebookPostHTML = `
          <div style="width: 100%; height: auto; background: transparent; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
            <!-- Header -->
            <div style="padding: 12px 16px; border-bottom: 1px solid #e4e6ea; display: flex; align-items: center; gap: 8px; background: white;">
              <div style="width: 40px; height: 40px; background: ${this.brandColor}; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 16px;">
                ${this.currentBrand.charAt(0)}
              </div>
              <div>
                <div style="font-weight: 600; font-size: 15px; color: #1c1e21;">${this.currentAccountName}</div>
                <div style="font-size: 13px; color: #65676b; display: flex; align-items: center; gap: 4px;">
                  ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long' })} at ${new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} · 
                  <svg width="12" height="12" fill="#65676b" viewBox="0 0 16 16">
                    <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zM3.5 6.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1 0-1z"/>
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Post Content -->
            <div style="padding: 12px 16px; background: white;">
              <p style="margin: 0 0 12px 0; font-size: 15px; line-height: 1.33; color: #1c1e21;">
                🥳 Welcoming ${customerName} to the ${this.currentAccountName} family. Congratulations on picking up your new ${this.currentBrand} ${vehicle}. 🚗 Thank you again for choosing us. 🤝
              </p>
              <p style="margin: 0; font-size: 15px; color: #385898; font-weight: 600;">
                #${this.currentAccountName.replace(/[^a-zA-Z0-9]/g, '')} #${this.currentBrand}${vehicle.replace(/[^a-zA-Z0-9]/g, '')} #NewCarDay
              </p>
            </div>
            
            <!-- Image -->
            ${customerPhoto ? `
            <div style="margin: 0; line-height: 0;">
              <img src="${customerPhoto}" alt="Customer Photo" style="width: 100%; height: auto; display: block; border-radius: 0;">
            </div>
            ` : `
            <div style="margin: 0; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 60px 20px; text-align: center; font-size: 16px; font-weight: 600;">
              <div style="font-size: 48px; margin-bottom: 12px;">📷</div>
              <div>Customer Photo</div>
              <div style="font-size: 12px; opacity: 0.8; margin-top: 8px;">Preview placeholder</div>
            </div>
            `}
            

            
            <!-- Engagement Section -->
            <div style="padding: 8px 16px; border-top: 1px solid #e4e6ea; background: white;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <div style="font-size: 13px; color: #65676b;">
                  👍 ❤️ 🎉 You and 47 others
                </div>
                <div style="font-size: 13px; color: #65676b;">
                  12 Comments · 8 Shares
                </div>
              </div>
              
              <div style="border-top: 1px solid #e4e6ea; padding-top: 8px; display: flex; justify-content: space-around;">
                <button style="background: none; border: none; color: #65676b; font-size: 15px; font-weight: 600; padding: 8px; cursor: pointer; display: flex; align-items: center; gap: 6px; flex: 1; justify-content: center;">
                  👍 Like
                </button>
                <button style="background: none; border: none; color: #65676b; font-size: 15px; font-weight: 600; padding: 8px; cursor: pointer; display: flex; align-items: center; gap: 6px; flex: 1; justify-content: center;">
                  💬 Comment
                </button>
                <button style="background: none; border: none; color: #65676b; font-size: 15px; font-weight: 600; padding: 8px; cursor: pointer; display: flex; align-items: center; gap: 6px; flex: 1; justify-content: center;">
                  📤 Share
                </button>
              </div>
            </div>
          </div>
        `
        
        return facebookPostHTML
      }
      
      // Fallback to original processing if no submission data
      let processedContent = htmlContent
      
      // Replace template placeholders with actual customer data if available
      if (this.latestSubmissionData) {
        // Get customer photo from field 13
        const customerPhoto = this.latestSubmissionData['13'] || this.recentCustomerPhoto || ''
        
        const replacements = {
          '{Buyers Name:1}': this.latestSubmissionData['1'] || '{Buyers Name:1}',
          '{Buyers Name}': this.latestSubmissionData['1'] || '{Buyers Name}',
          'Customer Name: {Buyers Name:1}': `Customer Name: ${this.latestSubmissionData['1'] || '{Buyers Name:1}'}`,
          '{Buyers Vehicle:4}': this.latestSubmissionData['4'] || '{Buyers Vehicle:4}', 
          '{Buyers Vehicle}': this.latestSubmissionData['4'] || '{Buyers Vehicle}',
          'Vehicle: {Buyers Vehicle:4}': `Vehicle: ${this.latestSubmissionData['4'] || '{Buyers Vehicle:4}'}`,
          '{Salesperson Name:9}': this.latestSubmissionData['9'] || '{Salesperson Name:9}',
          '{Salesperson Name}': this.latestSubmissionData['9'] || '{Salesperson Name}', 
          'Salesperson: {Salesperson Name:9}': `Salesperson: ${this.latestSubmissionData['9'] || '{Salesperson Name:9}'}`,
          '{Special Notes:4}': this.latestSubmissionData['5'] || '{Special Notes:4}', // Field 5 might be special notes
          '{Special Notes}': this.latestSubmissionData['5'] || '{Special Notes}',
          'Special Notes: {Special Notes:4}': `Special Notes: ${this.latestSubmissionData['5'] || '{Special Notes:4}'}`,
          '{Customer Photo:13}': customerPhoto || '{Customer Photo:13}',
          '{Customer Photo}': customerPhoto || '{Customer Photo}'
        }
        
        // Apply all replacements
        Object.entries(replacements).forEach(([placeholder, value]) => {
          processedContent = processedContent.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), value)
        })
      }
      
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = processedContent
      
      // Find all img tags
      const images = tempDiv.querySelectorAll('img')
      
      images.forEach(img => {
        const src = img.getAttribute('src')
        const alt = img.getAttribute('alt')
        
        // Get customer photo from latest submission or recent photo
        const customerPhoto = this.latestSubmissionData?.['13'] || this.recentCustomerPhoto
        
        // Check if image source contains "Customer Photo" or similar placeholders
        // Also check for placeholder patterns like {Customer Photo:13}
        const isPlaceholder = !src || 
          src.includes('{Customer Photo') || 
          src.includes('Customer Photo') || 
          src === '' || 
          src.startsWith('{') || 
          src.includes('undefined') ||
          src.match(/^\{.*:13\}$/i) // Matches {anything:13} pattern
        
        if (isPlaceholder) {
          // If we have a customer photo, use it
          if (customerPhoto) {
            img.setAttribute('src', customerPhoto)
            img.setAttribute('alt', alt || 'Customer Photo')
            img.style.width = '100%'
            img.style.height = 'auto'
            img.style.borderRadius = '8px'
            img.style.objectFit = 'cover'
            img.style.maxWidth = 'none'
            
            // Add error handler for invalid URLs (404 errors)
            img.onerror = () => {
              const placeholder = document.createElement('div')
              placeholder.style.cssText = `
                display: flex;
                align-items: center;
                justify-content: center;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                padding: 40px 20px;
                border-radius: 12px;
                text-align: center;
                font-family: 'Arial', sans-serif;
                font-size: 16px;
                font-weight: 600;
                margin: 10px 0;
                width: 100%;
                min-height: 120px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                border: 2px solid rgba(255,255,255,0.1);
              `
              placeholder.innerHTML = `
                <div style="text-align: center;">
                  <div style="font-size: 24px; margin-bottom: 8px;">📷</div>
                  <div>${alt || 'Customer Photo'}</div>
                  <div style="font-size: 12px; opacity: 0.8; margin-top: 4px;">Image not available</div>
                </div>
              `
              img.parentNode.replaceChild(placeholder, img)
            }
          } 
          // Otherwise, create a nice text placeholder
          else {
            const placeholder = document.createElement('div')
            placeholder.style.cssText = `
              display: flex;
              align-items: center;
              justify-content: center;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 40px 20px;
              border-radius: 12px;
              text-align: center;
              font-family: 'Arial', sans-serif;
              font-size: 16px;
              font-weight: 600;
              margin: 10px 0;
              width: 100%;
              min-height: 120px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              border: 2px solid rgba(255,255,255,0.1);
            `
            placeholder.innerHTML = `
              <div style="text-align: center;">
                <div style="font-size: 24px; margin-bottom: 8px;">📷</div>
                <div>${alt || 'Customer Photo'}</div>
                <div style="font-size: 12px; opacity: 0.8; margin-top: 4px;">Preview placeholder</div>
              </div>
            `
            img.parentNode.replaceChild(placeholder, img)
          }
        } else {
          // Even if src looks valid, add error handler for 404s
          img.onerror = () => {
            const placeholder = document.createElement('div')
            placeholder.style.cssText = `
              display: flex;
              align-items: center;
              justify-content: center;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 40px 20px;
              border-radius: 12px;
              text-align: center;
              font-family: 'Arial', sans-serif;
              font-size: 16px;
              font-weight: 600;
              margin: 10px 0;
              width: 100%;
              min-height: 120px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              border: 2px solid rgba(255,255,255,0.1);
            `
            placeholder.innerHTML = `
              <div style="text-align: center;">
                <div style="font-size: 24px; margin-bottom: 8px;">📷</div>
                <div>${alt || 'Image'}</div>
                <div style="font-size: 12px; opacity: 0.8; margin-top: 4px;">Image not available</div>
              </div>
            `
            img.parentNode.replaceChild(placeholder, img)
          }
        }
      })
      
      return tempDiv.innerHTML
    },

    adjustIframeHeight(event) {
      const iframe = event.target
      try {
        // Wait for content to load, then adjust height
        setTimeout(() => {
          const iframeDocument = iframe.contentDocument || iframe.contentWindow.document
          const body = iframeDocument.body
          const html = iframeDocument.documentElement
          
          // Get the actual content height
          const height = Math.max(
            body.scrollHeight,
            body.offsetHeight,
            html.clientHeight,
            html.scrollHeight,
            html.offsetHeight
          )
          
          // Set iframe height with some padding
          iframe.style.height = Math.max(height + 20, 600) + 'px'
        }, 100)
      } catch (error) {
        // If we can't access iframe content (CORS), use default height
        iframe.style.height = '800px'
      }
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.showToastMessage('HTML copied to clipboard!')
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.showToastMessage('HTML copied to clipboard!')
      }
    },

    showToastMessage(message) {
      this.toastMessage = message
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 3000)
    },

    getCurrentTime() {
      return new Date().toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
    },

    getSMSFromNumber() {
      // You can customize this based on your Twilio setup
      return '+61427985617' // This should match your Twilio phone number
    },

    getSMSToNumber() {
      // This would typically come from form data
      if (this.latestSubmissionData && this.latestSubmissionData['2']) {
        return this.latestSubmissionData['2'] // Assuming field 2 is phone number
      }
      return 'Mobile No:'
    },

    processSMSTemplate() {
      // Create a default SMS message template
      let smsMessage = `Hi {Buyers Name:1}, Congratulations on the purchase of your {Buyers Vehicle:4}...
We would love for you to leave us review at for ${this.currentAccountName} at https://search.google.com/local/writereview?placeid=ChIJf0T4kctr1moRtvlRzr-EskQ`

      // Replace with actual data if available
      if (this.latestSubmissionData) {
        const customerName = this.latestSubmissionData['1'] || 'Customer'
        const vehicle = this.latestSubmissionData['4'] || 'New Vehicle'
        
        smsMessage = `Hi ${customerName}, Congratulations on the purchase of your ${vehicle}...
We would love for you to leave us review for ${this.currentAccountName} at https://search.google.com/local/writereview?placeid=ChIJf0T4kctr1moRtvlRzr-EskQ`
      }

      return smsMessage
    }
  }
}
</script>

<style scoped>
/* Custom styles for better presentation */
.notification-template-viewer {
  min-height: 100vh;
}

/* Smooth transitions */
.notification-template-viewer * {
  transition: all 0.2s ease-in-out;
}

/* Code block styling */
pre code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.4;
}

/* Iframe styling */
iframe {
  background: white;
}

/* Toast animation */
.toast-enter-active, .toast-leave-active {
  transition: opacity 0.3s ease;
}

.toast-enter, .toast-leave-to {
  opacity: 0;
}

</style>
